/**
 * Accent Color Utilities
 *
 * Provides functions to manage main game accent colors and team-specific accent colors
 * Ensures text is always readable by automatically determining appropriate text color
 *
 * Main Accent: Used for the main game shell (player's team colors)
 * Team Accent: Used for content areas when viewing opposition/other teams
 */

/**
 * Converts a color to RGB values
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : { r: 0, g: 0, b: 0 };
}

/**
 * Converts RGB values to hex color string
 */
function rgbToHex(r: number, g: number, b: number): string {
    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase();
}

/**
 * Calculates luminance of a color for determining readable text color
 * Uses relative luminance formula from WCAG
 */
function getLuminance(hex: string): number {
    const { r, g, b } = hexToRgb(hex);
    const [rs, gs, bs] = [r, g, b].map(x => {
        x = x / 255;
        return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Determines if text should be light or dark based on background color
 * Returns '#ffffff' for dark backgrounds, '#000000' for light backgrounds
 */
export function getReadableTextColor(backgroundColor: string): string {
    const luminance = getLuminance(backgroundColor);
    return luminance > 0.5 ? '#000000' : '#ffffff';
}

/**
 * Adjusts color brightness by a multiplier
 * multiplier > 1 makes it brighter, < 1 makes it darker
 */
export function adjustBrightness(hex: string, multiplier: number): string {
    const { r, g, b } = hexToRgb(hex);
    const adjust = (value: number) => Math.min(255, Math.max(0, Math.round(value * multiplier)));
    return rgbToHex(adjust(r), adjust(g), adjust(b));
}

/**
 * Sets the main game accent color
 * This is used for the player's team colors throughout the main game interface
 */
export function setMainAccentColor(accentColor: string): void {
    const accentTextColor = getReadableTextColor(accentColor);
    const { r, g, b } = hexToRgb(accentColor);

    // Set main accent colors
    document.documentElement.style.setProperty('--accent-color', accentColor);
    document.documentElement.style.setProperty('--accent-color-rgb', `${r}, ${g}, ${b}`);
    document.documentElement.style.setProperty('--accent-text-color', accentTextColor);

    // Set accent shadow and borders
    document.documentElement.style.setProperty(
        '--accent-shadow',
        `0 0 20px rgba(${r}, ${g}, ${b}, 0.25), 0 4px 12px rgba(0, 0, 0, 0.3)`
    );
    document.documentElement.style.setProperty('--accent-border', `2px solid ${accentColor}`);
    document.documentElement.style.setProperty(
        '--accent-border-subtle',
        `1px solid rgba(${r}, ${g}, ${b}, 0.3)`
    );

    // Update legacy variables for compatibility
    document.documentElement.style.setProperty('--player-header-color', accentColor);
    document.documentElement.style.setProperty('--secondary-accent-color', accentTextColor);
}

/**
 * Sets the team accent color for content areas
 * This is used when viewing opposition teams or specific team pages
 * Auto-resets to match main accent when navigation leaves team views
 */
export function setTeamAccentColor(teamColor: string): void {
    const accentTextColor = getReadableTextColor(teamColor);
    const { r, g, b } = hexToRgb(teamColor);

    // Set team accent colors
    document.documentElement.style.setProperty('--accent-team-color', teamColor);
    document.documentElement.style.setProperty('--accent-team-color-rgb', `${r}, ${g}, ${b}`);
    document.documentElement.style.setProperty('--accent-team-text-color', accentTextColor);

    // Set team accent shadow and borders
    document.documentElement.style.setProperty(
        '--accent-team-shadow',
        `0 0 20px rgba(${r}, ${g}, ${b}, 0.25), 0 4px 12px rgba(0, 0, 0, 0.3)`
    );
    document.documentElement.style.setProperty('--accent-team-border', `2px solid ${teamColor}`);
    document.documentElement.style.setProperty(
        '--accent-team-border-subtle',
        `1px solid rgba(${r}, ${g}, ${b}, 0.3)`
    );

    // Update legacy content accent for compatibility
    document.documentElement.style.setProperty('--content-accent-color', teamColor);
    document.documentElement.style.setProperty('--content-accent-color-rgb', `${r}, ${g}, ${b}`);
}

/**
 * Resets team accent to match the main accent color
 * Called when navigating away from team-specific views
 */
export function resetTeamAccentColor(): void {
    const mainAccentColor = getComputedStyle(document.documentElement).getPropertyValue('--accent-color').trim();
    setTeamAccentColor(mainAccentColor);
}

/**
 * Gets the current main accent color
 */
export function getMainAccentColor(): string {
    return getComputedStyle(document.documentElement).getPropertyValue('--accent-color').trim();
}

/**
 * Gets the current team accent color
 */
export function getTeamAccentColor(): string {
    return getComputedStyle(document.documentElement).getPropertyValue('--accent-team-color').trim();
}

/**
 * Gets the readable text color for main accent
 */
export function getMainAccentTextColor(): string {
    return getComputedStyle(document.documentElement).getPropertyValue('--accent-text-color').trim();
}

/**
 * Gets the readable text color for team accent
 */
export function getTeamAccentTextColor(): string {
    return getComputedStyle(document.documentElement).getPropertyValue('--accent-team-text-color').trim();
}
